import React, { useEffect, useState } from "react";
import { Modal3 } from "../../../../../components/DIsplay/Modal/Modal3";
import Button1 from "../../../../../components/form/Button1";
import { enqueueSnackbar } from "notistack";
import { flightStatusMap } from "../../../../Orders/OrdersData";

/** `value` is what we send to the API; `label` is display-only */
export const FLIGHT_BOOKING_STATUS_OPTIONS = [
  "Booked",
  "Paid",
  "Pending",
  "PendingTicketIssue",
  "PendingTicketIssueApproval",
  "Issued",
  "Failed",
  "Expired",
  "AutoCanceled",
  "Canceled",
  "TicketCanceled",
  "TicketCancelationRequested",
  "TicketIssueDenied",
  "Refunded",
].map((value) => ({
  value,
  label: flightStatusMap(value) || value,
}));

function getStatusPillStyles(status) {
  switch (status) {
    case "Paid":
      return {
        bg: "bg-[#32CD32]/15",
        text: "text-[#1B8A1B]",
        border: "border-[#32CD32]",
        selectedBg: "bg-[#32CD32]",
        selectedText: "text-white",
        dot: "bg-[#32CD32]",
      };
    case "Issued":
      return {
        bg: "bg-[#009688]/15",
        text: "text-[#00796B]",
        border: "border-[#009688]",
        selectedBg: "bg-[#009688]",
        selectedText: "text-white",
        dot: "bg-[#009688]",
      };
    case "Refunded":
      return {
        bg: "bg-[#00BCD4]/15",
        text: "text-[#00838F]",
        border: "border-[#00BCD4]",
        selectedBg: "bg-[#00BCD4]",
        selectedText: "text-white",
        dot: "bg-[#00BCD4]",
      };
    case "Booked":
      return {
        bg: "bg-[#1E61DC]/15",
        text: "text-[#1E61DC]",
        border: "border-[#1E61DC]",
        selectedBg: "bg-[#1E61DC]",
        selectedText: "text-white",
        dot: "bg-[#1E61DC]",
      };
    case "Pending":
    case "PendingTicketIssue":
    case "PendingTicketIssueApproval":
    case "TicketCancelationRequested":
      return {
        bg: "bg-[#FE9F29]/15",
        text: "text-[#C77700]",
        border: "border-[#FE9F29]",
        selectedBg: "bg-[#FE9F29]",
        selectedText: "text-white",
        dot: "bg-[#FE9F29]",
      };
    case "Failed":
    case "Expired":
    case "Canceled":
    case "AutoCanceled":
    case "TicketCanceled":
    case "TicketIssueDenied":
      return {
        bg: "bg-[#D32F2F]/15",
        text: "text-[#B71C1C]",
        border: "border-[#D32F2F]",
        selectedBg: "bg-[#D32F2F]",
        selectedText: "text-white",
        dot: "bg-[#D32F2F]",
      };
    default:
      return {
        bg: "bg-gray-100",
        text: "text-gray-700",
        border: "border-gray-300",
        selectedBg: "bg-gray-700",
        selectedText: "text-white",
        dot: "bg-gray-500",
      };
  }
}

function StatusPill({ value, label, selected, onClick }) {
  const styles = getStatusPillStyles(value);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border text-sm font-medium transition-all ${
        selected
          ? `${styles.selectedBg} ${styles.selectedText} ${styles.border} ring-2 ring-offset-1 ring-black/10`
          : `${styles.bg} ${styles.text} ${styles.border} hover:opacity-90`
      }`}
    >
      <span
        className={`h-2 w-2 rounded-full shrink-0 ${
          selected ? "bg-white/90" : styles.dot
        }`}
      />
      {label}
    </button>
  );
}

function EditFlightBookingStatusModal({
  open,
  setOpen,
  currentStatus,
  onSave,
}) {
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (open) {
      setStatus(currentStatus || "");
    }
  }, [open, currentStatus]);

  const handleSave = () => {
    if (!status) {
      enqueueSnackbar("Please select a status", { variant: "warning" });
      return;
    }
    // Always save the API status value (e.g. "Booked"), never the display label
    onSave?.(status);
    setOpen(false);
  };

  return (
    <Modal3 open={open} setOpen={setOpen} title="Edit booking status">
      <div className="p-4 sm:p-6 flex flex-col gap-5 min-w-[280px] sm:min-w-[480px] max-w-[560px]">
        <div className="flex flex-col gap-2">
          <span className="text-sm text-gray-600">Select a new status</span>
          <div className="flex flex-wrap gap-2">
            {FLIGHT_BOOKING_STATUS_OPTIONS.map((option) => (
              <StatusPill
                key={option.value}
                value={option.value}
                label={option.label}
                selected={status === option.value}
                onClick={() => setStatus(option.value)}
              />
            ))}
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <Button1
            variant="outlined"
            className="!border-primary !text-primary"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button1>
          <Button1 onClick={handleSave}>Save</Button1>
        </div>
      </div>
    </Modal3>
  );
}

export default EditFlightBookingStatusModal;
